import cn from 'clsx'

import React, { useMemo } from 'react'
import { ALBUMS, SHOWCASES } from '@disco/constants/FeatureTypes'
import { BRIEFS, INBOX_TYPES } from '@disco/constants/GroupTypes'
import {
  ALBUM_VIEW,
  ARTIST_VIEW,
  BRIEF_CLOSED_VIEW,
  BRIEF_OPEN_VIEW,
  BRIEF_MINE_VIEW,
  BRIEF_ALL_VIEW,
} from '@disco/constants/ViewTypes'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import Tooltip from 'components/ui/tooltip'
import type { Option } from '../select-options'
import { libViewOptions } from '../select-options'
import styles from './index.module.css'

type Props = {
  groupType?: string
  value: string
  onChange: (value: string) => void
  options?: Array<Option>
}

const ViewSelector = ({
  groupType,
  value,
  onChange,
  options: customOptions,
}: Props): React.ReactElement<'div'> => {
  const albumsEnabled: boolean = useFeatureEnabled(ALBUMS)
  const showcasesEnabled: boolean = useFeatureEnabled(SHOWCASES)
  const options = useMemo(() => {
    if (customOptions) return customOptions

    let filteredOptions = libViewOptions
    const isInbox: boolean = groupType ? INBOX_TYPES.includes(groupType) : false
    const isBriefsList: boolean = groupType ? BRIEFS.includes(groupType) : false

    if (isBriefsList) {
      filteredOptions = [
        { label: 'All', value: BRIEF_ALL_VIEW },
        { label: 'Open', value: BRIEF_OPEN_VIEW },
        { label: 'Closed', value: BRIEF_CLOSED_VIEW },
        { label: 'Mine', value: BRIEF_MINE_VIEW },
      ]
    }

    if (!albumsEnabled || isInbox) {
      filteredOptions = filteredOptions.filter(
        (option: Option) => option.value !== ALBUM_VIEW,
      )
    }

    if (!showcasesEnabled || isInbox) {
      filteredOptions = filteredOptions.filter(
        (option: Option) => option.value !== ARTIST_VIEW,
      )
    }

    return filteredOptions
  }, [customOptions, albumsEnabled, groupType, showcasesEnabled])
  return (
    <div className={styles.viewSelector}>
      {options.map((option: Option, index: number) => {
        const link = (
          <div
            className={cn(styles.viewSelectorLink, {
              [styles.viewSelectorLinkSelected]: option.value === value,
              [styles.viewSelectorLinkDisabled]: option.disabled,
            })}
            onClick={() => !option.disabled && onChange(option.value)}
          >
            {option.label}
          </div>
        )
        return (
          <div className={styles.viewSelectorItem} key={index}>
            {option.disabled && option.tooltip ? (
              <Tooltip content={option.tooltip}>{link}</Tooltip>
            ) : (
              link
            )}
          </div>
        )
      })}
    </div>
  )
}

export default ViewSelector
